import { useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Button, Input, Select } from "../../../components/ui";
import { PROTOCOL_ACTIONS, type Protocol, type ProtocolActionId } from "../types";

const DELAY_KEYS = ["immediate", "after2min", "after5min", "after10min"] as const;

interface StepDraft {
  id: string;
  actionId: ProtocolActionId;
  delayKey: string;
}

export interface ProtocolFormModalProps {
  onClose: () => void;
  onCreate: (protocol: Protocol) => void;
}

export default function ProtocolFormModal({ onClose, onCreate }: ProtocolFormModalProps) {
  const { t } = useTranslation("common");

  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("");
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [errors, setErrors] = useState<{ name?: string; trigger?: string; steps?: string }>({});

  const addStep = (actionId: ProtocolActionId) => {
    if (steps.some((step) => step.actionId === actionId)) return;
    setSteps((prev) => [
      ...prev,
      { id: `${actionId}-${prev.length}`, actionId, delayKey: DELAY_KEYS[0] },
    ]);
    setErrors((prev) => ({ ...prev, steps: undefined }));
  };

  const removeStep = (id: string) => setSteps((prev) => prev.filter((step) => step.id !== id));

  const updateDelay = (id: string, delayKey: string) =>
    setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, delayKey } : step)));

  const submit = () => {
    const nextErrors: typeof errors = {};
    if (!name.trim()) nextErrors.name = t("monitoreoIa.protocols.nameRequired");
    if (!trigger.trim()) nextErrors.trigger = t("monitoreoIa.protocols.triggerRequired");
    if (steps.length === 0) nextErrors.steps = t("monitoreoIa.protocols.stepsRequired");

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onCreate({
      id: `proto-${name.trim().toLowerCase().replace(/\s+/g, "-")}`,
      name: name.trim(),
      trigger: trigger.trim(),
      enabled: true,
      steps: steps.map((step) => ({
        id: step.id,
        actionId: step.actionId,
        delay: t(`monitoreoIa.delays.${step.delayKey}`),
      })),
    });
  };

  return (
    <Modal
      title={t("monitoreoIa.protocols.newTitle")}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <Input
            label={t("monitoreoIa.protocols.nameLabel")}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={errors.name}
            placeholder={t("monitoreoIa.protocols.namePlaceholder")}
          />

          <Input
            label={t("monitoreoIa.protocols.triggerLabel")}
            required
            value={trigger}
            onChange={(event) => setTrigger(event.target.value)}
            error={errors.trigger}
            placeholder={t("monitoreoIa.protocols.triggerPlaceholder")}
          />

          <div>
            <p className="border-b border-line pb-2 text-sm font-semibold text-content">
              {t("monitoreoIa.protocols.actionsTitle")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {PROTOCOL_ACTIONS.map((actionId) => {
                const used = steps.some((step) => step.actionId === actionId);
                return (
                  <button
                    key={actionId}
                    type="button"
                    disabled={used}
                    onClick={() => addStep(actionId)}
                    className={`h-8 rounded-lg border px-3 text-xs font-medium transition-colors ${
                      used
                        ? "cursor-not-allowed border-line bg-subtle text-content-faint"
                        : "border-line bg-app text-content hover:bg-subtle"
                    }`}
                  >
                    <i className="ri-add-line mr-1" />
                    {t(`monitoreoIa.protocolActions.${actionId}`)}
                  </button>
                );
              })}
            </div>
            {errors.steps ? <p className="mt-1 text-xs text-error">{errors.steps}</p> : null}
          </div>

          {steps.length > 0 && (
            <ol className="space-y-2">
              {steps.map((step, index) => (
                <li
                  key={step.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl bg-surface px-3 py-2"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/15 text-xs font-semibold text-primary-500">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-content">
                    {t(`monitoreoIa.protocolActions.${step.actionId}`)}
                  </span>
                  <Select
                    value={step.delayKey}
                    onChange={(event) => updateDelay(step.id, event.target.value)}
                    size="sm"
                    options={DELAY_KEYS.map((key) => ({
                      value: key,
                      label: t(`monitoreoIa.delays.${key}`),
                    }))}
                    containerClassName="w-full sm:w-36"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    className="ml-auto"
                    onClick={() => removeStep(step.id)}
                    aria-label={t("monitoreoIa.protocols.removeStep")}
                    icon={<i className="ri-delete-bin-line" />}
                  />
                </li>
              ))}
            </ol>
          )}

          <div className="flex gap-3 border-t border-line pt-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-line px-4 text-sm font-medium text-content transition-colors hover:bg-subtle"
            >
              {t("monitoreoIa.protocols.cancel")}
            </button>
            <button
              type="button"
              onClick={submit}
              className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary-500 px-4 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              {t("monitoreoIa.protocols.save")}
            </button>
          </div>
        </div>
      }
    />
  );
}
